import {first, map} from 'rxjs/operators';
import {from, Observable} from 'rxjs';
import {collection, collectionData, doc, DocumentData, Firestore, getDocs, limit, orderBy, query, waitForPendingWrites} from '@angular/fire/firestore';
import {Timestamp} from '@firebase/firestore';

export function readDocumentWithId<T>(doc: Observable<DocumentData>): Observable<T> {

  return <any>doc
    .pipe(
      map(action => {

        if(!action) {
          return undefined;
        }

        return {...action, id: action.id}
      })
    );
}

export function readDocumentValue<T>(doc: Observable<DocumentData>): Observable<T> {

  return <any>doc
    .pipe(
      map(action => action)
    );
}


export function readCollectionWithIds<T>(col: Observable<DocumentData[]>): Observable<T> {

  return <any>col
  .pipe(
      map(snaps => snaps
        .filter(snap => snap.id !== undefined && snap.id !== null)
        .map(snap => {

          // return snap
          return {...snap, id: snap.id}
      })),
    first()
  );
}

/**
 *
 * reads a Query or a collection into an Observable, without creating a live connection to the database.
 *
 */

export function readDocs<T>(query: any): Observable<T[]> {
  return from(getDocs(query)).pipe(map(snaps => snaps.docs.map(snap => ({...<any>snap.data(), id: snap.id}))))
}

export function findUniqueMatchWithId<T>(col: Observable<(DocumentData | (DocumentData & {}))[]>): Observable<T | null> {
  return <any>col
    .pipe(
      map(results => {

        if (results.length > 1) {
          console.log("Collection",col);
          console.log("results",results);
          throw 'Found multiple matches to a unique result query v2.';
        }

        let result = null;

        if (results.length == 1) {

          result = { ...results[0], id: results[0].id }

        }
        return result;
      })
    );
}

export function findLastBySeqNo<T>(afs: Firestore, collectionPath: string): Observable<T> {

  const constraints = [
    orderBy('seqNo', 'desc'),
    limit(1)
  ]

  const collectionData$ = collectionData(query(collection((afs as Firestore), collectionPath), ...constraints), {idField: "id"});

  return findUniqueMatchWithId(collectionData$).pipe(first()) as Observable<T>

}

export function createFirestoreId(firestore: Firestore) {
  return doc(collection(firestore, '_')).id;
}

/**
 * Safely converts a Firestore date-ish value to a JS Date.
 * Handles a Firestore Timestamp (`.toDate()`), a serialized `{seconds}`/`{_seconds}`
 * object, an epoch-ms number, an ISO string, or an existing Date. Returns null if
 * the value cannot be interpreted (never throws - some backends/emulator seeds store
 * dates in a non-Timestamp shape, which used to crash `.toDate()` callers).
 */
export function toJsDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') {
    try { return value.toDate(); } catch { return null; }
  }
  const secs = value.seconds ?? value._seconds;
  if (typeof secs === 'number') {
    const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
    return new Date(secs * 1000 + Math.floor(nanos / 1e6));
  }
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export async function waitForPendingWritesWithTimeout(
  firestore: Firestore,
  timeoutMs = 3000
): Promise<boolean> {
  try {
    await Promise.race([
      waitForPendingWrites(firestore),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('waitForPendingWrites timeout')), timeoutMs)
      ),
    ]);
    return true;
  } catch (err) {
    // Never block navigation/redirect because of Firestore pending writes
    console.warn('[Firestore] waitForPendingWrites skipped:', err);
    return false;
  }
}

/**
 * Deep-copy a value with every `undefined` removed, because Firestore rejects `undefined` anywhere in a
 * written object and optional fields are simply absent rather than null.
 *
 * `Date` is excluded explicitly: it is `typeof 'object'` and not a Timestamp, but its fields are
 * non-enumerable getters, so `Object.entries(new Date())` is `[]` and the date would be silently reduced
 * to `{}` — a corrupt write with no error anywhere.
 */
export function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) return value.map(stripUndefined) as unknown as T;

  if (value && typeof value === 'object'
      && !(value instanceof Timestamp) && !(value instanceof Date)) {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      if (v !== undefined) out[k] = stripUndefined(v);
    });
    return out as T;
  }

  return value;
}
